import type { ComponentType } from 'react';

/**
 * Pane body. Must be used inside Pane. Thin wrapper around ContainerBody with
 * a built-in error boundary so errors from smart components (e.g. React Query)
 * are caught and a fallback is shown instead of breaking the grid.
 */
import { forwardRef } from 'react';
import type { FallbackProps } from 'react-error-boundary';

import { ContainerBody, type ContainerBodyProps } from '@frontend/components';

import { PaneErrorBoundary } from './PaneErrorBoundary';

export type PaneBodyProps = ContainerBodyProps & {
  /**
   * Custom error fallback. Receives error and resetErrorBoundary.
   * Defaults to PaneBodyFallback (message + "Try again" button).
   */
  fallback?: ComponentType<FallbackProps>;
  /**
   * Called when an error is caught. Useful for logging.
   */
  onError?: (error: Error, info: { componentStack: string | null }) => void;
};

export const PaneBody = forwardRef<HTMLDivElement, PaneBodyProps>(
  ({ children, fallback, onError, ...props }, ref) => (
    <ContainerBody
      ref={ref}
      padding={0}
      flex={1}
      justifyContent="stretch"
      alignItems="stretch"
      {...props}
    >
      <PaneErrorBoundary fallback={fallback} onError={onError}>
        {children}
      </PaneErrorBoundary>
    </ContainerBody>
  ),
);

PaneBody.displayName = 'PaneBody';
